/**
 * Modal Panel
 * 
 * Focused modal with minimize capability for tools like Pairwise, Dot Voting.
 * Part of Workstream 1: Navigation Restructure
 */

import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, MinusIcon, ArrowsPointingOutIcon } from '@heroicons/react/24/outline';
import { usePanels } from './PanelContext';

interface ModalPanelProps {
  id: string;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  onClose?: () => void;
  allowMinimize?: boolean;
  footer?: React.ReactNode;
}

const sizeClasses = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-4xl',
  full: 'sm:max-w-7xl',
};

export function ModalPanel({
  id,
  title,
  children,
  size = 'md',
  onClose,
  allowMinimize = true,
  footer,
}: ModalPanelProps) {
  const { isOpen, closePanel, minimizePanel } = usePanels();
  const open = isOpen(id);
  
  const handleClose = () => {
    if (onClose) onClose();
    closePanel(id);
  };
  
  const handleMinimize = () => {
    minimizePanel(id);
  };
  
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-[var(--surface)] bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel 
                className={`relative transform overflow-hidden  bg-[var(--bg-raised)] text-left shadow-xl transition-all sm:my-8 sm:w-full ${sizeClasses[size]}`}
              >
                {/* Header */}
                <div className="flex items-center justify-between border-b border-[var(--rule)] bg-[var(--surface)] px-4 py-3 sm:px-6">
                  <Dialog.Title className="text-base font-semibold leading-6 text-[var(--text-pri)]">
                    {title}
                  </Dialog.Title>
                  <div className="flex items-center space-x-2">
                    {allowMinimize && (
                      <button
                        type="button"
                        className="bg-[var(--surface)] text-[var(--text-ter)] hover:text-[var(--text-ter)] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        onClick={handleMinimize}
                        title="Minimize"
                      >
                        <span className="sr-only">Minimize</span>
                        <MinusIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                    )}
                    <button
                      type="button"
                      className="bg-[var(--surface)] text-[var(--text-ter)] hover:text-[var(--text-ter)] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      onClick={handleClose}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>
                </div>
                
                {/* Content */}
                <div className="max-h-[70vh] overflow-y-auto px-4 py-5 sm:p-6">
                  {children}
                </div>
                
                {/* Footer */}
                {footer && (
                  <div className="border-t border-[var(--rule)] bg-[var(--surface)] px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    {footer}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

// Minimized panel indicator
interface MinimizedPanelsBarProps {
  className?: string;
}

export function MinimizedPanelsBar({ className = '' }: MinimizedPanelsBarProps) {
  const { minimizedPanels, maximizePanel, closePanel } = usePanels();
  
  if (minimizedPanels.length === 0) return null;
  
  return (
    <div className={`fixed bottom-4 right-4 flex flex-col space-y-2 ${className}`}>
      {minimizedPanels.map(panel => (
        <div
          key={panel.id}
          className="flex items-center space-x-2 bg-[var(--bg-raised)] px-3 py-2 shadow-lg ring-1 ring-black ring-opacity-5"
        >
          <span className="text-sm font-medium text-[var(--text-pri)] truncate max-w-[150px]">
            {panel.title}
          </span>
          <button
            type="button"
            className="text-[var(--text-ter)] hover:text-[var(--text-ter)]"
            onClick={() => maximizePanel(panel.id)}
            title="Restore"
          >
            <ArrowsPointingOutIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="text-[var(--text-ter)] hover:text-[var(--text-ter)]"
            onClick={() => closePanel(panel.id)}
            title="Close"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );
}

// Standalone modal version
export function ModalPanelStandalone({
  open,
  onClose,
  title,
  children,
  size = 'md',
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  footer?: React.ReactNode;
}) {
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-[var(--surface)] bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel 
                className={`relative transform overflow-hidden  bg-[var(--bg-raised)] text-left shadow-xl transition-all sm:my-8 sm:w-full ${sizeClasses[size]}`}
              >
                <div className="flex items-center justify-between border-b border-[var(--rule)] bg-[var(--surface)] px-4 py-3 sm:px-6">
                  <Dialog.Title className="text-base font-semibold leading-6 text-[var(--text-pri)]">
                    {title}
                  </Dialog.Title>
                  <button
                    type="button"
                    className="bg-[var(--surface)] text-[var(--text-ter)] hover:text-[var(--text-ter)]"
                    onClick={onClose}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                <div className="max-h-[70vh] overflow-y-auto px-4 py-5 sm:p-6">
                  {children}
                </div>
                {footer && (
                  <div className="border-t border-[var(--rule)] bg-[var(--surface)] px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    {footer}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
